/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { LinearProgress, Chip, CircularProgress } from "@mui/material";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Custom components
import Dropdown from "components/CustomDropdown/Dropdown";
import PaginationBar from "components/PaginationBar/PaginationBar";

// Services
import { getAllCyclesForTutor } from "services/financeService";

// React imports
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// Data builders (for now using static, will be dynamic later)
import TutorsPayrollViewStudentTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewStudentTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";
import TutorsParollViewPaymentOrderTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewPaymentOrderTableData";

function TutorsPayrollView() {
  const { id } = useParams();

  // console.log("TutorsPayrollView component rendered, ID:", id);

  // State management
  const [loading, setLoading] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [allCycles, setAllCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("all");

  // Pagination states for different tables
  const [studentPage, setStudentPage] = useState(1);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(5);

  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);

  const resolveDefaultCycle = (cycles) => {
    if (!Array.isArray(cycles) || cycles.length === 0) return null;

    const now = new Date();
    const activeStatuses = ["ACTIVE", "PENDING", "APPROVED", "PROCESSING"];
    const toValidDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const getStartDate = (cycle) =>
      toValidDate(cycle?.cycleStartDate || cycle?.startDate || cycle?.start || cycle?.createdAt);
    const getEndDate = (cycle) =>
      toValidDate(cycle?.cycleEndDate || cycle?.endDate || cycle?.end || cycle?.updatedAt);

    const activeByDate = cycles.find((cycle) => {
      const startDate = getStartDate(cycle);
      const endDate = getEndDate(cycle);
      return startDate && endDate && startDate <= now && now <= endDate;
    });

    if (activeByDate) return activeByDate;

    const activeByStatus = cycles.find((cycle) =>
      activeStatuses.includes(String(cycle?.status || "").toUpperCase())
    );

    if (activeByStatus) return activeByStatus;

    const upcomingCycle = [...cycles]
      .filter((cycle) => {
        const startDate = getStartDate(cycle);
        return startDate && startDate >= now;
      })
      .sort((a, b) => getStartDate(a) - getStartDate(b))[0];

    return upcomingCycle || cycles[0] || null;
  };

  const getStudentId = (student) => String(student?._id || student?.id || "");

  const getUniqueStudents = (cycles) => {
    const studentsById = new Map();
    cycles.forEach((cycle) => {
      const students = cycle?.financeId?.students || [];
      students.forEach((student) => {
        const id = getStudentId(student);
        if (!id) return;
        if (!studentsById.has(id)) {
          studentsById.set(id, student);
        }
      });
    });
    return Array.from(studentsById.values());
  };

 const toSafeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getId = (value) => String(value?._id || value?.id || value || "");

const isPaidCycle = (cycle) => String(cycle?.status || "").toUpperCase() === "PAID";

const getCycleTutorRate = (cycle) => {
  if (!cycle) return 0;

  const finance = cycle?.financeId || {};
  const classSessionRaw = finance?.classSessionId;
  const classSession = Array.isArray(classSessionRaw)
    ? classSessionRaw[0]
    : classSessionRaw || {};

  const cycleTutorId = getId(cycle?.tutorId);
  const currentTutorId = getId(classSession?.tutorId || finance?.tutorId);
  const oldTutorId = getId(
    finance?.oldTutorDetails?._id ||
      finance?.lastPastTutorId ||
      classSession?.pastTutors?.[0]
  );

  const classCycles = Array.isArray(classSession?.cycles) ? classSession.cycles : [];
  const matchedClassCycle = classCycles.find(
    (item) => item?.name === `Cycle ${cycle?.cycleNumber}`
  );

  const originalCycleClasses =
    toSafeNumber(matchedClassCycle?.totalNoOfClasses) ||
    toSafeNumber(classSession?.totalNoOfClasses) ||
    toSafeNumber(cycle?.totalClasses) ||
    1;

  const oldRateFromCycle =
    originalCycleClasses > 0
      ? toSafeNumber(matchedClassCycle?.tutorPay) / originalCycleClasses
      : 0;

  const oldRateFromCompleted =
    toSafeNumber(cycle?.completedClasses) > 0
      ? toSafeNumber(cycle?.calculatedAmount) / toSafeNumber(cycle?.completedClasses)
      : 0;

  const isOldTutor =
    (oldTutorId && cycleTutorId && oldTutorId === cycleTutorId) ||
    (currentTutorId && cycleTutorId && currentTutorId !== cycleTutorId);

  if (isOldTutor) {
    return (
      toSafeNumber(finance?.oldTutorPerClassSalary) ||
      oldRateFromCompleted ||
      oldRateFromCycle ||
      0
    );
  }

  return (
    toSafeNumber(cycle?.tutorPayRate) ||
    oldRateFromCompleted ||
    toSafeNumber(finance?.tutorPayRate) ||
    toSafeNumber(classSession?.tutorSalary) ||
    oldRateFromCycle ||
    0
  );
};

const getStudentTotals = (cycles = []) => {
  const totalsByStudentId = new Map();

  cycles.forEach((cycle) => {
    const students = cycle?.financeId?.students || [];
    const classSession = cycle?.financeId?.classSessionId;
    const classCycles = Array.isArray(classSession?.cycles) ? classSession.cycles : [];

    const matchedClassCycle = classCycles.find(
      (item) => item?.name === `Cycle ${cycle?.cycleNumber}`
    );

    const originalCycleClasses =
      toSafeNumber(matchedClassCycle?.totalNoOfClasses) ||
      toSafeNumber(classSession?.totalNoOfClasses) ||
      toSafeNumber(cycle?.totalClasses);

    const currentCycleClasses = toSafeNumber(cycle?.totalClasses);

    const feePerClass =
      originalCycleClasses > 0
        ? toSafeNumber(matchedClassCycle?.monthlyFee || classSession?.paymentAmount) /
          originalCycleClasses
        : 0;

    const tutorPayPerClass = getCycleTutorRate(cycle);

    const cycleTotalFee = feePerClass * currentCycleClasses;
    const cycleTutorPay = tutorPayPerClass * currentCycleClasses;

    students.forEach((student) => {
      const id = getStudentId(student);
      if (!id) return;

      const existing = totalsByStudentId.get(id) || {
        totalFees: 0,
        totalTutorPay: 0,
        totalOwedSalary: 0,
      };

      const completedClasses = toSafeNumber(cycle?.completedClasses);
      const calculatedAmount = toSafeNumber(cycle?.calculatedAmount);
      const baseOwedSalary =
        calculatedAmount > 0
          ? calculatedAmount
          : tutorPayPerClass * completedClasses;
      const cycleOwedSalary = isPaidCycle(cycle)
        ? baseOwedSalary - toSafeNumber(cycle?.deductionAmount)
        : 0;

      totalsByStudentId.set(id, {
        totalFees: existing.totalFees + cycleTotalFee,
        totalTutorPay: existing.totalTutorPay + cycleTutorPay,
        totalOwedSalary: existing.totalOwedSalary + cycleOwedSalary,
      });
    });
  });

  return totalsByStudentId;
};

  const allStudents = getUniqueStudents(allCycles);
  const studentTotalsById = getStudentTotals(allCycles);

  const filteredCycles =
    selectedStudentId === "all"
      ? allCycles
      : allCycles.filter((cycle) =>
          (cycle?.financeId?.students || []).some(
            (student) => getStudentId(student) === selectedStudentId
          )
        );

  const getCycleLabel = (cycle) => {
    const students = cycle?.financeId?.students || [];
    if (students.length === 0) {
      return `Cycle ${cycle.cycleNumber} (${cycle.status})`;
    }
    const firstName = students[0]?.fullName || students[0]?.name || "Student";
    const suffix = students.length > 1 ? ` +${students.length - 1}` : "";
    return `Cycle ${cycle.cycleNumber} (${cycle.status}) - ${firstName}${suffix}`;
  };

  // Build dynamic student table data with pagination
  const buildStudentTableData = () => {
    if (!allStudents || allStudents.length === 0) {
      return { columns: [], rows: [], totalStudents: 0 };
    }

    const columns = [
      { name: "sno", align: "center" },
      { name: "student name", align: "left" },
      // { name: "school name", align: "left" },
      // { name: "board", align: "center" },
      // { name: "class", align: "center" },
      { name: "subject", align: "center" },
      // { name: "gender", align: "center" },
      // { name: "date of birth", align: "center" },
      // { name: "preferred time duration", align: "center" },
      // { name: "term", align: "center" },
      { name: "total fees", align: "center" },
      { name: "tutor pay", align: "center" },
      { name: "owed salary", align: "center" },
      { name: "profit", align: "center" },
      { name: "location city", align: "center" },
    ];

    const students = allStudents;

    // Helper function to format date
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const rows = students.map((student, index) => {
      const studentId = getStudentId(student);
      const totals = studentTotalsById.get(studentId) || {
        totalFees: 0,
        totalTutorPay: 0,
        totalOwedSalary: 0,
      };
      const totalProfit = totals.totalFees - totals.totalTutorPay;
      const profitPercentage = totals.totalFees > 0 ? (totalProfit / totals.totalFees) * 100 : 0;
      const profitColor = totalProfit >= 0 ? "success" : "error";

      return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      "student name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.name || student.fullName || "N/A"}
        </ArgonTypography>
      ),
      // "school name": (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.schoolCollegeName || student.schoolName || "N/A"}
      //   </ArgonTypography>
      // ),
      // class: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.class?.name || student.class || "N/A"}
      //   </ArgonTypography>
      // ),
      subject: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.subjects && student.subjects.length > 0
            ? student.subjects.map((sub) => sub.name || sub).join(", ")
            : "N/A"}
        </ArgonTypography>
      ),
      // board: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.board?.name || student.board || "N/A"}
      //   </ArgonTypography>
      // ),

      // gender: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.gender || "N/A"}
      //   </ArgonTypography>
      // ),
      // "date of birth": (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {formatDate(student.dob)}
      //   </ArgonTypography>
      // ),
      // "preferred time duration": (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.preferredTimeDuration || "N/A"} min
      //   </ArgonTypography>
      // ),
      // term: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {student.term || "N/A"}
      //   </ArgonTypography>
      // ),
      "total fees": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹ {Math.round(totals.totalFees)}
        </ArgonTypography>
      ),
      "tutor pay": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹ {Math.round(totals.totalTutorPay)}
        </ArgonTypography>
      ),
      "owed salary": (
        <ArgonTypography variant="caption" color="warning" fontWeight="bold">
          ₹ {Math.round(totals.totalOwedSalary)}
        </ArgonTypography>
      ),
      profit: (
        <ArgonTypography variant="caption" color={profitColor} fontWeight="medium">
          ₹ {Math.round(totalProfit)} ({Math.round(profitPercentage)}%)
        </ArgonTypography>
      ),
      "location city": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {payrollData?.financeId?.parentId?.city || "N/A"}
        </ArgonTypography>
      ),
    };
    });

    return { columns, rows, totalStudents: students.length };
  };

  const { columns, rows, totalStudents } = buildStudentTableData();

  // Build dynamic credit details table data
  const buildCreditDetailsTableData = (cycles = []) => {
    const columns = [
      { name: "Cycle Name", align: "left" },
      { name: "Total Classes", align: "center" },
      { name: "Completed Classes", align: "center" },
      { name: "Status", align: "center" },
    ];

    const rows = cycles.map((cycle, index) => ({
      "Cycle Name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Cycle {cycle.cycleNumber || index + 1}
        </ArgonTypography>
      ),
      "Total Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.totalClasses || 0}
        </ArgonTypography>
      ),
      "Completed Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.completedClasses || 0}
        </ArgonTypography>
      ),
      Status: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.status || "N/A"}
        </ArgonTypography>
      ),
    }));

    return { columns, rows };
  };

  const { columns: creditColumns, rows: creditRows } = buildCreditDetailsTableData(filteredCycles);
  const { columns: columns1, rows: rows1 } = CreditMappingViewCreditTableData;
  const { columns: columns2, rows: rows2 } = TutorsParollViewPaymentOrderTableData;

  // Fetch payroll data by ID
  const fetchPayrollData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      // Fetch all cycles for this tutor
      const response = await getAllCyclesForTutor(id);
      if (response.success && response.data && response.data.length > 0) {
        const toValidDate = (value) => {
          if (!value) return null;
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? null : date;
        };
        const getStartDate = (cycle) =>
          toValidDate(
            cycle?.cycleStartDate || cycle?.startDate || cycle?.start || cycle?.createdAt
          );

        // Sort cycles by start date (earliest first), fallback to createdAt
        const sortedCycles = [...response.data].sort((a, b) => {
          const aDate = getStartDate(a);
          const bDate = getStartDate(b);
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return aDate - bDate;
        });

        setAllCycles(sortedCycles);
        const defaultCycle = resolveDefaultCycle(sortedCycles);

        const firstStudent = getUniqueStudents(sortedCycles)[0];
        if (firstStudent) {
          setSelectedStudentId(getStudentId(firstStudent));
        } else {
          setSelectedStudentId("all");
        }

        console.log("Payroll Data:", defaultCycle);
        console.log("Finance ID:", defaultCycle?.financeId);
        console.log("ClassSession Data:", defaultCycle?.financeId?.classSessionId);
      }
    } catch (error) {
      console.error("Error fetching payroll data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, [id]);

  useEffect(() => {
    if (filteredCycles.length > 0) {
      const defaultCycle = resolveDefaultCycle(filteredCycles);
      if (defaultCycle) {
        setPayrollData(defaultCycle);
        setSelectedCycle(defaultCycle._id);
      } else {
        setPayrollData(null);
        setSelectedCycle("");
      }
    } else {
      setPayrollData(null);
      setSelectedCycle("");
    }
  }, [selectedStudentId, allCycles]);

  // Handle cycle selection change
  const handleCycleChange = (e) => {
    const cycleId = e.target.value;
    setSelectedCycle(cycleId);
    const selectedCycleData = filteredCycles.find((cycle) => cycle._id === cycleId);
    if (selectedCycleData) {
      setPayrollData(selectedCycleData);
      // Reset pagination when cycle changes
      setStudentPage(1);
      setPaymentPage(1);
    }
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    setSelectedStudentId(studentId);
    setStudentPage(1);
    setPaymentPage(1);
  };

  // Pagination handlers for students table
  const handleStudentPageChange = (event, newPage) => {
    setStudentPage(newPage);
  };

  const handleStudentRowsPerPageChange = (event) => {
    setStudentRowsPerPage(parseInt(event.target.value, 10));
    setStudentPage(1);
  };

  // Pagination handlers for payment table
  const handlePaymentPageChange = (event, newPage) => {
    setPaymentPage(newPage);
  };

  const handlePaymentRowsPerPageChange = (event) => {
    setPaymentRowsPerPage(parseInt(event.target.value, 10));
    setPaymentPage(1);
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Extract data from API response
  const tutorName = payrollData?.tutorId?.fullName || "N/A";
  const tutorEmail = payrollData?.tutorId?.email || "";
  const tutorPhone = payrollData?.tutorId?.phone || "";
  const tutorStatus = payrollData?.status || "PENDING";
  const cycleType = payrollData?.cycleType || "MONTHLY";

  // Backend model uses 'calculatedAmount' as base amount
  const totalAmount = payrollData?.calculatedAmount || 0;
  const bonusAmount = payrollData?.bonusAmount || 0;
  const deductionAmount = payrollData?.deductionAmount || 0;
  const finalAmount = totalAmount + bonusAmount - deductionAmount;

  // Get status color
  const getStatusColor = () => {
    switch (tutorStatus) {
      case "PAID":
        return "linear-gradient(135deg, #43a047, #26a69a)";
      case "APPROVED":
        return "linear-gradient(135deg, #2196f3, #1976d2)";
      case "PENDING":
        return "linear-gradient(135deg, #f59e0b, #f97316)";
      case "REJECTED":
      case "FAILED":
        return "linear-gradient(135deg, #ef4444, #dc2626)";
      default:
        return "linear-gradient(135deg, #6b7280, #4b5563)";
    }
  };

  const parentName = payrollData?.financeId?.parentId?.fullName || "N/A";

  // Calculate fee charge and tutor salary per class (similar to payroll.js)
  // Try to get data from classSessionId first (if populated), otherwise calculate from financeId

const classSessionData = payrollData?.financeId?.classSessionId;
const packageCycles = Array.isArray(classSessionData?.cycles) ? classSessionData.cycles : [];

const selectedCycleInfo = packageCycles.find(
  (cycle) => cycle?.name === `Cycle ${payrollData?.cycleNumber}`
);

const originalCycleClasses =
  toSafeNumber(selectedCycleInfo?.totalNoOfClasses) ||
  toSafeNumber(classSessionData?.totalNoOfClasses) ||
  toSafeNumber(payrollData?.totalClasses) ||
  1;

const totalClasses = toSafeNumber(payrollData?.totalClasses);
const completedClasses = Math.min(
  toSafeNumber(payrollData?.completedClasses),
  totalClasses
);

const feeCharge =
  originalCycleClasses > 0
    ? toSafeNumber(selectedCycleInfo?.monthlyFee || classSessionData?.paymentAmount) /
      originalCycleClasses
    : 0;
const tutorSalary = getCycleTutorRate(payrollData);
const totalPackageAmount = feeCharge * totalClasses;
const totalPackageTutorSalary = tutorSalary * totalClasses;

const totalSessionsInClass = totalClasses;

const expectedTotalTutorPay = totalPackageTutorSalary;
const tutorPayEarned = tutorSalary * completedClasses;

const classProgressPercentage =
  totalClasses > 0 ? Math.round((completedClasses / totalClasses) * 100) : 0;

const sessionsPerWeek = toSafeNumber(
  classSessionData?.numberOfClassesInAWeek || classSessionData?.weeklyClasses
);
const hoursPerSession = classSessionData?.duration
  ? toSafeNumber(classSessionData.duration) / 60
  : 1.5;

const noOfMonths = toSafeNumber(classSessionData?.noOfMonths) || 1;
const totalPackageClasses = toSafeNumber(
  classSessionData?.totalNoOfClasses || classSessionData?.totalClasses
);
const sessionsPerMonth =
  noOfMonths > 0 && totalPackageClasses > 0
    ? Math.round(totalPackageClasses / noOfMonths)
    : sessionsPerWeek > 0
    ? Math.round(sessionsPerWeek * 4)
    : totalClasses;

const totalPackageProfit = totalPackageAmount - totalPackageTutorSalary;
const totalPackageProfitPercentage =
  totalPackageAmount > 0 ? (totalPackageProfit / totalPackageAmount) * 100 : 0;

// console.log("Filtered Cycles:", filteredCycles);
// console.log("Package Amount:", totalPackageAmount);
// console.log("Tutor Salary:", totalPackageTutorSalary);
// console.log("Profit:", totalPackageProfit);
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card
            sx={{
              mt: 3,
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              p: 3,
            }}
          >
            {/* Title */}
            <ArgonBox mb={3}>
              <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                {"Tutor's Payroll Management"}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                {"Manage tutor status, cycle, and payroll details"}
              </ArgonTypography>
            </ArgonBox>

            {/* Tutor Info Row */}
            <ArgonBox display="flex" alignItems="center" justifyContent="flex-start" gap={4} mb={3}>
              <ArgonTypography variant="body1" fontWeight="medium" color="dark">
                <strong>{"Tutor's Name:"}</strong> {tutorName}
              </ArgonTypography>

              <ArgonBox
                display="flex"
                alignItems="center"
                px={2}
                py={0.5}
                sx={{
                  background: getStatusColor(),
                  borderRadius: "12px",
                  boxShadow: "0 4px 10px rgba(67,160,71,0.25)",
                }}
              >
                <ArgonTypography
                  variant="caption"
                  fontWeight="bold"
                  color="white"
                  sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
                >
                  {tutorStatus}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            {/* Cycle Selector */}
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <Dropdown
                label="Select Student"
                options={[
                  { value: "all", label: "All Students" },
                  ...allStudents.map((student) => ({
                    value: getStudentId(student),
                    label: student.fullName || student.name || "Student",
                  })),
                ]}
                value={selectedStudentId}
                onChange={handleStudentChange}
              />
              <Dropdown
                label="Select Cycle"
                options={filteredCycles.map((cycle) => ({
                  value: cycle._id,
                  label: getCycleLabel(cycle),
                }))}
                value={selectedCycle}
                onChange={handleCycleChange}
              />
              <ArgonTypography variant="body2" color="text" fontWeight="medium">
                Cycle Type: <strong>{cycleType}</strong>
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" fontWeight="medium">
                Total Cycles: <strong>{filteredCycles.length}</strong>
              </ArgonTypography>
            </ArgonBox>
          </Card>

          <ArgonBox py={3}>
            <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
              Student List ({totalStudents} Students)
            </ArgonTypography>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </ArgonBox>

            {/* Pagination for students
            {totalStudents > 0 && (
              <ArgonBox mt={2} px={3}>
                <PaginationBar
                  currentPage={studentPage}
                  rowsPerPage={studentRowsPerPage}
                  totalItems={totalStudents}
                  onPageChange={handleStudentPageChange}
                  onRowsPerPageChange={handleStudentRowsPerPageChange}
                />
              </ArgonBox>
            )} */}
          </ArgonBox>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                <ArgonBox
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                    Package
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Package pricing and tutor salary details
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={3}>
                {/* Hourly Rates */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                      Class Rates
                    </ArgonTypography>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Fee Charge per class
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                        ₹ {Math.round(feeCharge)}
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Tutor Salary per class
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                        ₹ {Math.round(tutorSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Sessions */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                      Sessions
                    </ArgonTypography>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Sessions in a week
                      </ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                        {sessionsPerWeek} sessions |{" "}
                        {Math.round(sessionsPerWeek * hoursPerSession * 10) / 10} hours
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Sessions in a month
                      </ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                        {sessionsPerMonth} sessions |{" "}
                        {Math.round(sessionsPerMonth * hoursPerSession * 10) / 10} hours
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Total Package Summary */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Total Package Summary
                      </ArgonTypography>
                    </ArgonBox>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Total Fee
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {Math.round(totalPackageAmount)}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Payscale
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {Math.round(totalPackageTutorSalary)}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Profit
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="success">
                            ₹ {Math.round(totalPackageProfit)} (
                            {Math.round(totalPackageProfitPercentage)}%)
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    </Grid>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Tutor Info */}
              <ArgonBox mt={3}>
                <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                  Tutor Name - {tutorName}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">
                  Email: {tutorEmail} | Phone: {tutorPhone}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          </Card>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                  Attendance Overview
                </ArgonTypography>

                {/* <ArgonBox display="flex" gap={2}>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    sx={{
                      backgroundColor: "rgb(8, 2, 120)",
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      fontWeight: "bold",
                      borderRadius: "10px",
                    }}
                    href="/admin/tutorpayroll/view/1/101"
                  >
                    Details View
                  </ArgonButton>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    sx={{
                      backgroundColor: "rgb(8, 2, 120)",
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      fontWeight: "bold",
                      borderRadius: "10px",
                    }}
                    href="/admin/creditmapping/creditmapping-view/tutors-replacement"
                  >
                    Replace Tutors
                  </ArgonButton>
                </ArgonBox> */}
              </ArgonBox>

              {/* Progress Section */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={5}>
                  <ArgonBox>
                    {(() => {
                      const completed = Number(payrollData?.completedClasses) || 0;
                      const total = Number(payrollData?.totalClasses) || 0;
                      const percentage = total > 0 ? (completed / total) * 100 : 0;

                      console.log(
                        "Progress Bar Debug - Completed:",
                        completed,
                        "Total:",
                        total,
                        "Calculated Percentage:",
                        percentage
                      );

                      return (
                        <>
                          <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                            No. Of Sessions Completed {completed} / {total}
                          </ArgonTypography>

                          {/* Custom Progress Bar */}
                          <ArgonBox
                            sx={{
                              width: "100%",
                              height: 12,
                              backgroundColor: "#f0f0f0",
                              borderRadius: "6px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <ArgonBox
                              sx={{
                                width: `${percentage}%`,
                                height: "100%",
                                backgroundColor: "#1976d2",
                                borderRadius: "6px",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </ArgonBox>

                          <ArgonTypography
                            variant="caption"
                            color="dark"
                            fontWeight="medium"
                            mt={1}
                          >
                            {Math.round(percentage)}%
                          </ArgonTypography>
                        </>
                      );
                    })()}
                  </ArgonBox>
                </Grid>

                <Grid item xs={12} md={2}></Grid>

                <Grid item xs={12} md={5}>
                  <ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                      Remaining Sessions
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      {(payrollData?.totalClasses || 0) - (payrollData?.completedClasses || 0)}
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Expiry Warning */}
              {(() => {
                const totalClasses = payrollData?.totalClasses || 0;
                const completedClasses = payrollData?.completedClasses || 0;
                const thresholdPercentage = 75;
                const thresholdSessions = Math.ceil((totalClasses * thresholdPercentage) / 100);
                const sessionsNeeded = Math.max(0, thresholdSessions - completedClasses);
                const completionPercentage =
                  totalClasses > 0 ? Math.round((completedClasses / totalClasses) * 100) : 0;

                if (completionPercentage >= thresholdPercentage) {
                  return (
                    <ArgonBox
                      mt={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: { xs: "100%", md: "fit-content" },
                        margin: "16px auto 0",
                        px: { xs: 2, md: 4 },
                        border: "2px solid #4caf50",
                        borderRadius: "12px",
                        p: 1.5,
                        backgroundColor: "#f0fff4",
                      }}
                    >
                      <i
                        className="fa-solid fa-check-circle"
                        style={{ color: "green", fontSize: "22px", marginRight: "12px" }}
                      ></i>
                      <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                        {"Tutor's salary is now due for payment."}
                      </ArgonTypography>
                    </ArgonBox>
                  );
                }

                return (
                  <ArgonBox
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: { xs: "100%", md: "fit-content" },
                      margin: "16px auto 0",
                      px: { xs: 2, md: 4 },
                      border: "2px solid #f44336",
                      borderRadius: "12px",
                      p: 1.5,
                      backgroundColor: "#fff5f5",
                    }}
                  >
                    <i
                      className="fa-solid fa-triangle-exclamation"
                      style={{ color: "red", fontSize: "22px", marginRight: "12px" }}
                    ></i>
                    <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                      {`Tutor's salary will be due after ${sessionsNeeded} more session${
                        sessionsNeeded !== 1 ? "s" : ""
                      } (${completionPercentage}% completed, ${thresholdPercentage}% required).`}
                    </ArgonTypography>
                  </ArgonBox>
                );
              })()}
            </ArgonBox>
          </Card>

          <Card
            sx={{
              mt: 3,
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                background: "linear-gradient(45deg, #667eea, #764ba2)",
                borderRadius: "50%",
                opacity: 0.05,
              }}
            />

            <ArgonBox p={4}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                    Credit Counter
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track credits, usage and payments
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={4}>
                {/* Progress Bar Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "#f8f9fa",
                      borderRadius: "16px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      justifyContent="center"
                    >
                      {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Usage Progress
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        width: 180,
                        height: 180,
                        margin: "0 auto",
                        mb: 2,
                      }}
                    >
                      <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{ stroke: "#e0e0e0" }}
                        />
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{
                            stroke: "url(#gradient)",
                            strokeDasharray: "289.027px, 289.027px",
                            strokeDashoffset: `${
                              289.027 - (289.027 * classProgressPercentage) / 100
                            }px`,
                            strokeLinecap: "round",
                          }}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: "#764ba2", stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <text
                          x="50"
                          y="45"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {classProgressPercentage}%
                        </text>
                        <text
                          x="50"
                          y="58"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#6c757d", fontSize: "6px" }}
                        >
                          Completed
                        </text>
                      </svg>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                        ₹{Math.round(tutorPayEarned)} / ₹{Math.round(expectedTotalTutorPay)}
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="#6c757d">
                        Monthly Pay Scale
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="success.main" fontWeight="medium">
                                              {completedClasses} / {totalClasses} Classes Completed
                                            </ArgonTypography>
                    </ArgonBox>
                    
                  </ArgonBox>
                </Grid>

                {/* Table Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "white",
                      borderRadius: "16px",
                      p: 3,
                      boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                      {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Credit Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={creditColumns} rows={creditRows} />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={2} alignItems="stretch">
                {/* Account Summary */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Account Summary
                      </ArgonTypography>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="primary">
                          ₹{totalPackageTutorSalary}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Total Classes:
                        </ArgonTypography>
                        <Chip
                          label={totalSessionsInClass}
                          size="small"
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Usually Pays By:
                        </ArgonTypography>
                        <Chip
                          label="UPI"
                          size="small"
                          sx={{
                            bgcolor: "success.main",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Payment Details */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Payment Details
                      </ArgonTypography>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Base Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                          ₹{totalAmount || 0}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Bonus Amount:
                        </ArgonTypography>
                        <Chip
                          label={`₹${bonusAmount}`}
                          size="small"
                          sx={{
                            bgcolor: "success.main",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Deduction:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="error.dark">
                          ₹{deductionAmount}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        sx={{
                          borderTop: "2px solid #e0e0e0",
                          pt: 1.5,
                          mt: 1,
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="bold">
                          Final Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                          ₹{finalAmount}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Bank Details */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Cycle Summary
                      </ArgonTypography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Cycle Type:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                              {cycleType}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Total Minutes:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                              {payrollData?.totalMinutes || 0} mins
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Final Amount:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                              ₹ {finalAmount}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>
                      </Grid>
                    </ArgonBox>
                  </Card>
                </Grid>
              </Grid>

              {/* Payment Cycle Summary */}
              <Card
                sx={{
                  mt: 4,
                  borderRadius: "16px",
                  backgroundColor: "white",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                }}
              >
                <ArgonBox p={3}>
                  <ArgonTypography variant="h5" fontWeight="bold" color="dark" mb={3}>
                    Payment Cycle Details
                  </ArgonTypography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Cycle Number
                        </ArgonTypography>
                        <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                          #{payrollData?.cycleNumber || 0}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Cycle Start Date
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {payrollData?.cycleStartDate
                            ? new Date(payrollData.cycleStartDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Cycle End Date
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {payrollData?.cycleEndDate
                            ? new Date(payrollData.cycleEndDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Total Minutes
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {payrollData?.totalMinutes || 0} mins
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Calculated Amount
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          ₹ {payrollData?.calculatedAmount || 0}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <ArgonTypography variant="caption" color="text">
                          Payment Status
                        </ArgonTypography>
                        <Chip
                          label={tutorStatus}
                          sx={{
                            background: getStatusColor(),
                            color: "white",
                            fontWeight: "bold",
                            mt: 1,
                            fontSize: "0.75rem",
                          }}
                        />
                      </ArgonBox>
                    </Grid>

                    {payrollData?.paymentDate && (
                      <Grid item xs={12} md={4}>
                        <ArgonBox
                          sx={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                          }}
                        >
                          <ArgonTypography variant="caption" color="text">
                            Payment Date
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            {new Date(payrollData.paymentDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    )}

                    {payrollData?.transactionId && (
                      <Grid item xs={12} md={4}>
                        <ArgonBox
                          sx={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                          }}
                        >
                          <ArgonTypography variant="caption" color="text">
                            Transaction ID
                          </ArgonTypography>
                          <ArgonTypography
                            variant="caption"
                            fontWeight="bold"
                            color="dark"
                            display="block"
                            mt={1}
                          >
                            {payrollData.transactionId}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    )}

                    {payrollData?.notes && (
                      <Grid item xs={12}>
                        <ArgonBox
                          sx={{
                            backgroundColor: "#fff3cd",
                            borderRadius: "12px",
                            p: 2,
                            border: "1px solid #ffeaa7",
                          }}
                        >
                          <ArgonTypography variant="caption" color="text" fontWeight="bold">
                            Notes:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" color="text" mt={1}>
                            {payrollData.notes}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    )}
                  </Grid>
                </ArgonBox>
              </Card>

              {/* <ArgonBox mt={4}>
                <ArgonTypography mb={2} variant="body2" fontWeight="medium" color="dark">
                  Total Payment Orders
                </ArgonTypography>

                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={columns2} rows={rows2} />
                </ArgonBox>
              </ArgonBox> */}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TutorsPayrollView;
